/**
 * 全局错误处理系统
 * 提供错误类型定义、错误分类和友好的错误显示
 */

/**
 * 错误类型枚举
 */
export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',     // 连接失败、DNS 失败
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',     // 请求超时
  AUTH_ERROR = 'AUTH_ERROR',           // 认证失败
  BUSINESS_ERROR = 'BUSINESS_ERROR',   // 业务错误
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',     // 未知错误
}

/**
 * 错误严重级别
 */
export enum ErrorSeverity {
  LOW = 'LOW',           // 低级别：可重试
  MEDIUM = 'MEDIUM',     // 中级别：影响功能但可继续
  HIGH = 'HIGH',         // 高级别：阻塞核心流程
  CRITICAL = 'CRITICAL', // 严重：完全无法使用
}

/**
 * 应用错误基类
 */
export class AppError extends Error {
  constructor(
    public type: ErrorType,
    public severity: ErrorSeverity,
    message: string,
    public originalError?: unknown,
    public userMessage?: string
  ) {
    super(message);
    this.name = 'AppError';
  }
}

/**
 * 网络错误
 */
export class NetworkError extends AppError {
  constructor(originalError?: unknown) {
    super(
      ErrorType.NETWORK_ERROR,
      ErrorSeverity.HIGH,
      '网络连接失败',
      originalError,
      '无法连接到服务器，请检查网络设置'
    );
    this.name = 'NetworkError';
  }
}

/**
 * 超时错误
 */
export class TimeoutError extends AppError {
  constructor(timeout: number) {
    super(
      ErrorType.TIMEOUT_ERROR,
      ErrorSeverity.MEDIUM,
      `请求超时 (${timeout}ms)`,
      undefined,
      '请求超时，请稍后重试'
    );
    this.name = 'TimeoutError';
  }
}

/**
 * 认证错误
 */
export class AuthError extends AppError {
  constructor(reason: 'unauthorized' | 'expired') {
    const messages = {
      unauthorized: '未登录',
      expired: '登录已过期'
    };
    super(
      ErrorType.AUTH_ERROR,
      ErrorSeverity.HIGH,
      `认证失败: ${reason}`,
      undefined,
      messages[reason]
    );
    this.name = 'AuthError';
  }
}

/**
 * 业务错误
 */
export class BusinessError extends AppError {
  constructor(message: string, userMessage?: string) {
    super(
      ErrorType.BUSINESS_ERROR,
      ErrorSeverity.MEDIUM,
      message,
      undefined,
      userMessage || message
    );
    this.name = 'BusinessError';
  }
}

/**
 * 错误上下文
 */
export interface ErrorContext {
  route?: string;
  action?: string;
  retryable?: boolean;
  retryFn?: () => void;
}

/**
 * 显示全屏错误页面
 */
export function showFullScreenError(
  error: AppError,
  context?: ErrorContext
): void {
  const app = document.getElementById('app');
  if (!app) return;

  const canRetry = context?.retryable && context?.retryFn;

  app.innerHTML = `
    <div class="error-page">
      <div class="error-container">
        <div class="error-icon">${getErrorIcon(error.type)}</div>
        <h1 class="error-title">${getErrorTitle(error.type)}</h1>
        <p class="error-message">${error.userMessage || error.message}</p>
        ${getErrorTips(error.type)}
        <div class="error-actions">
          ${canRetry ? `
            <button class="btn btn-primary" id="retryBtn">重试</button>
            <button class="btn btn-secondary" onclick="location.reload()">刷新页面</button>
          ` : `
            <button class="btn btn-primary" onclick="location.reload()">刷新页面</button>
          `}
        </div>
      </div>
    </div>
  `;

  // 绑定重试按钮
  if (canRetry && context?.retryFn) {
    const retryBtn = document.getElementById('retryBtn') as HTMLButtonElement;
    retryBtn?.addEventListener('click', () => {
      // 显示加载状态
      retryBtn.textContent = '重试中...';
      retryBtn.disabled = true;
      context.retryFn!();
    });
  }
}

/**
 * 获取错误图标
 */
function getErrorIcon(type: ErrorType): string {
  const icons = {
    [ErrorType.NETWORK_ERROR]: '📡',
    [ErrorType.TIMEOUT_ERROR]: '⏱️',
    [ErrorType.AUTH_ERROR]: '🔐',
    [ErrorType.BUSINESS_ERROR]: '⚠️',
    [ErrorType.UNKNOWN_ERROR]: '❌'
  };
  return icons[type] || icons[ErrorType.UNKNOWN_ERROR];
}

/**
 * 获取错误标题
 */
function getErrorTitle(type: ErrorType): string {
  const titles = {
    [ErrorType.NETWORK_ERROR]: '网络连接失败',
    [ErrorType.TIMEOUT_ERROR]: '请求超时',
    [ErrorType.AUTH_ERROR]: '认证失败',
    [ErrorType.BUSINESS_ERROR]: '操作失败',
    [ErrorType.UNKNOWN_ERROR]: '发生错误'
  };
  return titles[type] || titles[ErrorType.UNKNOWN_ERROR];
}

/**
 * 获取错误提示信息
 */
function getErrorTips(type: ErrorType): string {
  const tips = {
    [ErrorType.NETWORK_ERROR]: `
      <div class="error-tips">
        <p class="tip-title">可能的原因：</p>
        <ul class="tip-list">
          <li>网络连接不稳定</li>
          <li>服务器暂时无法访问</li>
          <li>防火墙阻止了连接</li>
        </ul>
      </div>
    `,
    [ErrorType.TIMEOUT_ERROR]: `
      <div class="error-tips">
        <p class="tip-title">建议操作：</p>
        <ul class="tip-list">
          <li>检查网络连接</li>
          <li>稍后再试</li>
        </ul>
      </div>
    `,
    [ErrorType.AUTH_ERROR]: '',
    [ErrorType.BUSINESS_ERROR]: '',
    [ErrorType.UNKNOWN_ERROR]: `
      <div class="error-tips">
        <p class="tip-title">建议操作：</p>
        <ul class="tip-list">
          <li>刷新页面重试</li>
          <li>检查网络连接</li>
        </ul>
      </div>
    `
  };
  return tips[type] || '';
}
